import { CARD_DEBUFF_CAP_BPS, cardReduce, cardStrongerDebuff } from './balance';

/**
 * **O que as cartas deixam a correr sobre um casco** (gate V28H.L).
 *
 * ## Porque um módulo neutro, e não um campo em cada modo
 *
 * Porque as mesmas oito coisas acontecem nos dois sítios. Uma Lentidão reduz
 * movimento numa arena e reduz movimento numa caçada; um Bastião torna
 * inalvejável para um adversário e para um mob. Escrever isto duas vezes seria
 * escrever duas regras que divergem na primeira correcção - e `pvp/` não pode
 * sequer importar `hunt/`, portanto a única casa possível é esta.
 *
 * ## Tudo é um TEMPORIZADOR, e um temporizador é dois números
 *
 * Quanto falta, e quanto vale. Não há uma lista de penalidades activas por
 * casco: há uma ranhura por TIPO, e a §1 do equilíbrio diz o que acontece quando
 * duas chegam à mesma ranhura - **fica a maior, e empatar renova**.
 *
 * Uma ranhura por tipo é o que torna a instantânea de tamanho fixo e a
 * re-simulação trivial. Uma lista cresceria com quantas cartas foram lançadas, e
 * um documento de tamanho variável é um documento que um dia não cabe.
 *
 * ## Nada aqui decide arte, e nada aqui sorteia
 *
 * Não há um único `Math.random`. Uma penalidade dura o que a tabela diz, vale o
 * que a tabela diz, e acaba no tique em que o contador chega a zero - nos dois
 * modos, em qualquer máquina.
 */

/** Um temporizador: quanto falta, e quanto vale enquanto durar. */
export interface CardTimer {
  /** Milissegundos restantes. `0` significa inactivo. */
  remainingMs: number;
  /** A magnitude em basis points. Sem sentido quando `remainingMs` é `0`. */
  magnitudeBps: number;
}

/** As oito ranhuras de um casco. Uma por tipo, e nunca uma lista. */
export interface CardEffectState {
  /** Movimento reduzido: Lentidão. */
  readonly slow: CardTimer;
  /** Cadência reduzida: Curto-Circuito. */
  readonly attackBreak: CardTimer;
  /** Dano causado reduzido: Marca da Caveira. */
  readonly damageBreak: CardTimer;
  /** Cura recebida reduzida: Malware. */
  readonly malware: CardTimer;
  /** Parado e sem disparar: Congelamento. */
  readonly frozen: CardTimer;
  /** Movimento aumentado: Sobremarcha. */
  readonly haste: CardTimer;
  /** Não pode ser escolhido como alvo: Bastião. */
  readonly untargetable: CardTimer;
  /** Invisível: Manto Espectral. */
  readonly cloak: CardTimer;
}

function timer(): CardTimer {
  return { remainingMs: 0, magnitudeBps: 0 };
}

/** Um casco sem nada a correr. */
export function initialCardEffects(): CardEffectState {
  return {
    slow: timer(),
    attackBreak: timer(),
    damageBreak: timer(),
    malware: timer(),
    frozen: timer(),
    haste: timer(),
    untargetable: timer(),
    cloak: timer(),
  };
}

/** As oito ranhuras, pela ordem canónica. A instantânea usa-a, e os testes também. */
export const CARD_EFFECT_SLOTS = [
  'slow',
  'attackBreak',
  'damageBreak',
  'malware',
  'frozen',
  'haste',
  'untargetable',
  'cloak',
] as const;

export type CardEffectSlot = (typeof CARD_EFFECT_SLOTS)[number];

/**
 * Põe uma penalidade ou um benefício numa ranhura.
 *
 * **A maior ganha; empatar renova.** Uma magnitude menor do que a que já lá está
 * é DESCARTADA por inteiro - nem o tempo dela sobrevive - porque deixar o tempo
 * de uma penalidade fraca prolongar uma forte seria somar pela porta do lado.
 *
 * Devolve `true` quando a ranhura mudou, para quem quiser emitir um evento.
 */
export function applyCardTimer(slot: CardTimer, durationMs: number, magnitudeBps: number): boolean {
  if (durationMs <= 0) {
    return false;
  }
  if (slot.remainingMs > 0) {
    const stronger = cardStrongerDebuff(slot.magnitudeBps, magnitudeBps);
    if (stronger > magnitudeBps) {
      /* A que lá está é mais forte: a nova não faz nada, nem sequer renova. */
      return false;
    }
    if (stronger === slot.magnitudeBps && magnitudeBps === slot.magnitudeBps) {
      /* Empate: renova, e fica com o mais longo dos dois. */
      slot.remainingMs = Math.max(slot.remainingMs, durationMs);
      return true;
    }
  }
  slot.remainingMs = durationMs;
  slot.magnitudeBps = magnitudeBps;
  return true;
}

/**
 * Faz correr as oito ranhuras por um passo, e diz quais ACABARAM.
 *
 * O passo é o tique de quem chama - 16 ms na caça, 50 ms no duelo - e nunca um
 * relógio de parede. Devolve as ranhuras que passaram de activas a inactivas
 * neste passo, para quem chama poder desfazer o que elas seguravam.
 */
export function ageCardEffects(state: CardEffectState, stepMs: number): CardEffectSlot[] {
  const ended: CardEffectSlot[] = [];
  for (const slot of CARD_EFFECT_SLOTS) {
    const entry = state[slot];
    if (entry.remainingMs <= 0) {
      continue;
    }
    entry.remainingMs = Math.max(0, entry.remainingMs - stepMs);
    if (entry.remainingMs === 0) {
      entry.magnitudeBps = 0;
      ended.push(slot);
    }
  }
  return ended;
}

/** Apaga tudo. A morte encerra o estado, e um destroço não carrega penalidades. */
export function clearCardEffects(state: CardEffectState): void {
  for (const slot of CARD_EFFECT_SLOTS) {
    state[slot].remainingMs = 0;
    state[slot].magnitudeBps = 0;
  }
}

/* -------------------------------------------------------------------------- */
/*  As perguntas que a simulação faz                                          */
/* -------------------------------------------------------------------------- */

/** Está congelado? Parado, e sem disparar. */
export function cardIsFrozen(state: CardEffectState): boolean {
  return state.frozen.remainingMs > 0;
}

/**
 * **Pode ser ESCOLHIDO como alvo?**
 *
 * A pergunta que qualquer coisa que decide um alvo faz: um míssil, um drone, o
 * alvo automático da caça, e uma carta com alvo. Duas cartas respondem que não,
 * e por razões diferentes - o Bastião porque é inalvejável, o Manto porque não
 * se vê. **Não se escolhe o que não se vê**, e é essa a razão de o Manto entrar
 * aqui.
 */
export function cardIsSelectable(state: CardEffectState): boolean {
  return state.untargetable.remainingMs === 0 && state.cloak.remainingMs === 0;
}

/**
 * **Um projétil pode ACERTAR neste casco?**
 *
 * Uma pergunta diferente da de cima, e a diferença é a decisão de equilíbrio
 * mais importante destas duas cartas.
 *
 * - **o Bastião responde que não.** "Inalvejável" durante quatro segundos tem de
 *   querer dizer que nada aterra: num duelo os tiros são apontados à mão, e uma
 *   carta que só impedisse mísseis e drones seria uma carta *perfect* com
 *   cinquenta segundos de arrefecimento que quase nunca faz nada;
 * - **o Manto responde que sim.** Ele já é nove segundos, o dobro, e atacar é a
 *   única coisa que o encerra. Se os tiros também o atravessassem, seriam nove
 *   segundos de imunidade - e a carta é furtividade, não é um escudo. Não se
 *   acerta no que não se vê, o que já é a defesa dele.
 *
 * Nem um nem outro é invulnerabilidade, e é aí que a linha fica: **uma
 * explosão, uma hemorragia e um veneno continuam a cobrar**. Uma explosão não
 * escolhe ninguém - cobre uma área - e uma parcela de veneno já tem o seu alvo
 * desde que foi colocada. É essa a fraqueza do Bastião, e ela é deliberada.
 */
export function cardIsHittable(state: CardEffectState): boolean {
  return state.untargetable.remainingMs === 0;
}

/** Está invisível? Só o Manto, e só para o desenho. */
export function cardIsCloaked(state: CardEffectState): boolean {
  return state.cloak.remainingMs > 0;
}

/**
 * Atacar encerra o Manto. **APROVADO**, e é a única maneira de o perder.
 *
 * Levar dano não revela: a descrição aprovada diz que atacar encerra o efeito, e
 * mais nada. Devolve `true` quando havia mesmo um manto para encerrar.
 */
export function cardBreakCloak(state: CardEffectState): boolean {
  if (state.cloak.remainingMs === 0) {
    return false;
  }
  state.cloak.remainingMs = 0;
  state.cloak.magnitudeBps = 0;
  return true;
}

/**
 * A velocidade de movimento depois das cartas, em bps.
 *
 * Congelado é zero - parado é parado, e nenhuma Sobremarcha o levanta. Fora
 * isso: o bónus multiplica, a penalidade reduz, e a ordem é essa. Tudo inteiro,
 * com uma truncagem por operação.
 */
export function cardMoveSpeedBps(state: CardEffectState, baseBps: number): number {
  if (cardIsFrozen(state)) {
    return 0;
  }
  let value = baseBps;
  if (state.haste.remainingMs > 0) {
    value = Math.trunc((value * (10_000 + state.haste.magnitudeBps)) / 10_000);
  }
  if (state.slow.remainingMs > 0) {
    value = cardReduce(value, state.slow.magnitudeBps);
  }
  return value;
}

/**
 * **Este congelamento também trava o ATAQUE?** (gate V28H.W).
 *
 * O Congelamento parava sempre as duas coisas. Passou a parar sempre o
 * movimento e a travar o ataque em trinta por cento das vezes, e a resposta
 * viaja na MAGNITUDE da própria ranhura: cheia é paralisia total, zero é só o
 * movimento.
 *
 * Na magnitude e não num campo novo, e é o que faz isto atravessar tudo sem
 * mudar uma única forma: a instantânea já guarda `bps` por ranhura, o restauro
 * já o lê, e a re-simulação já o repete. Um campo novo seria um campo novo em
 * cada uma dessas três fronteiras para dizer um bit.
 */
export function cardFreezeBlocksAttack(state: CardEffectState): boolean {
  return state.frozen.remainingMs > 0 && state.frozen.magnitudeBps >= CARD_DEBUFF_CAP_BPS;
}

/** A cadência de ataque depois das cartas. Só a paralisia TOTAL não dispara. */
export function cardAttackSpeedBps(state: CardEffectState, baseBps: number): number {
  if (cardFreezeBlocksAttack(state)) {
    return 0;
  }
  if (state.attackBreak.remainingMs === 0) {
    return baseBps;
  }
  return cardReduce(baseBps, state.attackBreak.magnitudeBps);
}

/**
 * O dano que este casco CAUSA, depois da Marca da Caveira.
 *
 * Em `bigint`, porque é o que a vida e o dano são desde o gate V28G.S.1: uma
 * conversão para `number` aqui truncaria uma progressão sem tecto.
 */
export function cardDamageOutMilli(state: CardEffectState, milli: bigint): bigint {
  if (state.damageBreak.remainingMs === 0) {
    return milli;
  }
  const capped = BigInt(Math.min(state.damageBreak.magnitudeBps, CARD_DEBUFF_CAP_BPS));
  return (milli * (10_000n - capped)) / 10_000n;
}

/** A cura que este casco RECEBE, depois do Malware. */
export function cardHealingInMilli(state: CardEffectState, milli: bigint): bigint {
  if (state.malware.remainingMs === 0) {
    return milli;
  }
  const capped = BigInt(Math.min(state.malware.magnitudeBps, CARD_DEBUFF_CAP_BPS));
  return (milli * (10_000n - capped)) / 10_000n;
}

/** Alguma coisa está a correr? Para a instantânea poder omitir o caso comum. */
export function cardEffectsIdle(state: CardEffectState): boolean {
  return CARD_EFFECT_SLOTS.every((slot) => state[slot].remainingMs === 0);
}
